import {
  IconBasketDiscount,
  IconBox,
  IconCurrencyDollar,
  IconMoneybag,
} from '@tabler/icons-react'
import { ArrowLeft } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import {
  Loading,
  MetricCard,
  ScalrBreadcrumb,
  ScalrCarousel,
} from '@/components/customComponents'
import FilterBar from '@/components/FilterBar'
import { createTab, TabConfig } from '@/components/TabSkeleton'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsList } from '@/components/ui/tabs'

import { metricCardColors } from '@/constants/COLOR_PALETTE'

import { toast, useSpendLensItemMetricCard, useTabAlerts } from '@/hooks'
import {
  selectPageRefreshTrigger,
  useAppSelector,
  useReduxBaseFilter,
} from '@/store'
import { dateToString, decodeAndFormatPageId } from '@/utils'

import { ComplianceTab } from '@/screens/clarityLens/spendLens/ComplianceTab'
import { MOCK_METRIC_CARDS_DATA_1 } from '@/screens/clarityLens/spendLens/CONSTANTS'
import { OverviewTab } from '@/screens/clarityLens/spendLens/OverviewTab'
import { PriceHistoryTab } from '@/screens/clarityLens/spendLens/PriceHistoryTab'
import { SpendTab } from '@/screens/clarityLens/spendLens/SpendTab'
import { TheoreticalVsActualTab } from '@/screens/clarityLens/spendLens/TheoreticalVsActualTab'

const SpendLensContent = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const [pageId] = useState<{
    categoryName: string
    itemName: string
    materialCode: string
  } | null>(() => {
    return decodeAndFormatPageId(id, 'spend-lens')
  })

  const handleBack = () => {
    navigate('/clarity-lens/spend-lens')
  }

  // Material Codes specific tab names
  const materialCodesTabs = [
    'Overview',
    'Spend',
    'SKU Breakdown',
    'Price History',
    'Compliance',
    'Theoretical vs Actual',
  ]

  const { tabAlertStatus, markTabAlertsAsRead } = useTabAlerts(
    pageId?.materialCode || '',
    'Spend Lens',
    materialCodesTabs
  )

  const tabConfigs: TabConfig[] = [
    {
      value: 'overview',
      title: t('clarityLens.salesLens.tabs.overview'),
      hasUnreadAlerts: tabAlertStatus['Overview']?.hasUnread,
      onTabClick: () => markTabAlertsAsRead('Overview'),
      content: (
        <>
          {pageId?.itemName ? (
            <OverviewTab
              itemName={pageId?.materialCode}
              clarityLensType={t('clarityLens.navigation.spendLens')}
            />
          ) : (
            <Loading isContainer />
          )}
        </>
      ),
    },
    {
      value: 'spend',
      title: t('clarityLens.spendLens.tabs.spend'),
      hasUnreadAlerts: tabAlertStatus['Spend']?.hasUnread,
      onTabClick: () => markTabAlertsAsRead('Spend'),
      content: (
        <>
          {pageId?.itemName ? (
            <SpendTab itemName={pageId?.materialCode} />
          ) : (
            <Loading isContainer />
          )}
        </>
      ),
    },
    // {
    //   value: 'skuBreakdown',
    //   title: 'SKU Breakdown',
    //   hasUnreadAlerts: tabAlertStatus['SKU Breakdown']?.hasUnread,
    //   onTabClick: () => markTabAlertsAsRead('SKU Breakdown'),
    //   content: (
    //     <>
    //       {pageId?.itemName ? (
    //         <SkuBreakdownTab itemName={pageId?.materialCode} />
    //       ) : (
    //         <Loading isContainer />
    //       )}
    //     </>
    //   ),
    // },
    {
      value: 'priceHistory',
      title: t('clarityLens.spendLens.tabs.priceHistory'),
      hasUnreadAlerts: tabAlertStatus['Price History']?.hasUnread,
      onTabClick: () => markTabAlertsAsRead('Price History'),
      content: (
        <>
          {pageId?.itemName ? (
            <PriceHistoryTab itemName={pageId?.materialCode} />
          ) : (
            <Loading isContainer />
          )}
        </>
      ),
    },
    {
      value: 'compliance',
      title: t('clarityLens.spendLens.tabs.compliance'),
      hasUnreadAlerts: tabAlertStatus['Compliance']?.hasUnread,
      onTabClick: () => markTabAlertsAsRead('Compliance'),
      content: (
        <>
          {pageId?.itemName ? (
            <ComplianceTab itemName={pageId?.materialCode} />
          ) : (
            <Loading isContainer />
          )}
        </>
      ),
    },
    {
      value: 'theoreticalActual',
      title: t('clarityLens.spendLens.tabs.theoreticalVsActual'),
      hasUnreadAlerts: tabAlertStatus['Theoretical vs Actual']?.hasUnread,
      onTabClick: () => markTabAlertsAsRead('Theoretical vs Actual'),
      content: (
        <>
          {pageId?.itemName ? (
            <TheoreticalVsActualTab itemName={pageId?.materialCode} />
          ) : (
            <Loading isContainer />
          )}
        </>
      ),
    },
  ]

  const tabs = tabConfigs.map((config) => createTab(config))

  const { selectedBranch, date } = useReduxBaseFilter('spend-lens-item')
  const refreshTrigger = useAppSelector(
    selectPageRefreshTrigger('spend-lens-item')
  )

  const {
    spendLensItemMetricCardData,
    trendData,
    loading: spendLensItemMetricCardLoading,
    error: spendLensItemMetricCardError,
    refetch: spendLensItemMetricCardRefetch,
  } = useSpendLensItemMetricCard(
    dateToString(date, 'from'),
    dateToString(date, 'to'),
    selectedBranch,
    pageId?.materialCode || '' // lowest level of the hierarchy
  )

  // Refetch data when refresh trigger is triggered
  useEffect(() => {
    if (refreshTrigger) {
      console.log('Refreshing spend lens item metric card...')
      spendLensItemMetricCardRefetch()
    }
  }, [refreshTrigger, spendLensItemMetricCardRefetch])

  // Show error toast if there is an error
  useEffect(() => {
    if (spendLensItemMetricCardError) {
      toast({
        title: t('common.errors.serverError'),
        description: spendLensItemMetricCardError?.message,
        variant: 'destructive',
      })
    }
  }, [spendLensItemMetricCardError])

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex flex-row justify-between mb-4">
          <div className="flex items-center">
            <Button onClick={handleBack} className="mr-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('clarityLens.salesLens.back')}
            </Button>
            <CardTitle>
              {pageId?.materialCode}{' '}
              {spendLensItemMetricCardData &&
                `(${spendLensItemMetricCardData?.unit})`}
            </CardTitle>
          </div>
        </div>
        <ScalrBreadcrumb
          breadcrumbItems={[
            { label: t('clarityLens.navigation.clarityLens') },
            {
              label: t('clarityLens.navigation.spendLens'),
              href: '/clarity-lens/spend-lens',
            },
          ]}
          title={pageId?.categoryName}
        />
      </CardHeader>
      <CardContent className="space-y-3">
        <FilterBar pageId="spend-lens-item" />

        <div>
          <div className="space-y-3 mb-10">
            <ScalrCarousel>
              <MetricCard
                title={t('clarityLens.spendLens.metrics.totalSpend')}
                type="money"
                content={spendLensItemMetricCardData?.totalSpend || 0}
                quarterToDate
                mtdCurrContent={spendLensItemMetricCardData?.totalSpend}
                ytdCurrContent={spendLensItemMetricCardData?.totalSpend}
                mtdPrevContent={trendData?.prevMetricsQuarterly?.totalSpend}
                ytdPrevContent={trendData?.prevMetricsYearly?.totalSpend}
                icon={<IconMoneybag color="white" size={20} />}
                color={metricCardColors.blue500}
                loading={spendLensItemMetricCardLoading}
              />
              <MetricCard
                title={t('clarityLens.spendLens.metrics.spendShare')}
                type="percentage"
                content={spendLensItemMetricCardData?.spendSharePercentage || 0}
                quarterToDate
                mtdCurrContent={
                  spendLensItemMetricCardData?.spendSharePercentage
                }
                ytdCurrContent={
                  spendLensItemMetricCardData?.spendSharePercentage
                }
                mtdPrevContent={
                  trendData?.prevMetricsQuarterly?.spendSharePercentage
                }
                ytdPrevContent={
                  trendData?.prevMetricsYearly?.spendSharePercentage
                }
                icon={<IconCurrencyDollar color="white" size={20} />}
                color={metricCardColors.orange500}
                loading={spendLensItemMetricCardLoading}
              />
              <MetricCard
                title={t('clarityLens.spendLens.metrics.averagePricePerUnit', {
                  unit: spendLensItemMetricCardData?.unit || '',
                })}
                type="money"
                content={spendLensItemMetricCardData?.averageUnitPrice || 0}
                quarterToDate
                mtdCurrContent={spendLensItemMetricCardData?.averageUnitPrice}
                ytdCurrContent={spendLensItemMetricCardData?.averageUnitPrice}
                mtdPrevContent={
                  trendData?.prevMetricsQuarterly?.averageUnitPrice
                }
                ytdPrevContent={trendData?.prevMetricsYearly?.averageUnitPrice}
                icon={<IconBox color="white" size={20} />}
                color={metricCardColors.green500}
                loading={spendLensItemMetricCardLoading}
              />
              <MetricCard
                title={t('clarityLens.spendLens.metrics.activeSkus')}
                type="number"
                content={spendLensItemMetricCardData?.activeSkuCount || 0}
                quarterToDate
                mtdCurrContent={spendLensItemMetricCardData?.activeSkuCount}
                ytdCurrContent={spendLensItemMetricCardData?.activeSkuCount}
                mtdPrevContent={trendData?.prevMetricsQuarterly?.activeSkuCount}
                ytdPrevContent={trendData?.prevMetricsYearly?.activeSkuCount}
                icon={<IconBasketDiscount color="white" size={20} />}
                color={metricCardColors.purple500}
                loading={spendLensItemMetricCardLoading}
              />
              <MetricCard
                title={t('clarityLens.spendLens.metrics.theoreticalVsActual')}
                type="percentage"
                content={MOCK_METRIC_CARDS_DATA_1.averagePriceUnit.content || 0}
                quarterToDate
                mtdCurrContent={
                  MOCK_METRIC_CARDS_DATA_1.averagePriceUnit.mtdCurrContent
                }
                ytdCurrContent={
                  MOCK_METRIC_CARDS_DATA_1.averagePriceUnit.ytdCurrContent
                }
                mtdPrevContent={
                  MOCK_METRIC_CARDS_DATA_1.averagePriceUnit.mtdPrevContent
                }
                ytdPrevContent={
                  MOCK_METRIC_CARDS_DATA_1.averagePriceUnit.ytdPrevContent
                }
                icon={<IconBasketDiscount color="white" size={20} />}
                color={metricCardColors.red500}
                loading={!MOCK_METRIC_CARDS_DATA_1}
                isBeta
              />
            </ScalrCarousel>
          </div>

          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="w-full">
              {tabs.map((tab) => tab.trigger)}
            </TabsList>

            {tabs.map((tab) => tab.content)}
          </Tabs>
        </div>
      </CardContent>
    </Card>
  )
}

const SpendLensPage = () => {
  return (
    <>
      <SpendLensContent />
    </>
  )
}

export default SpendLensPage
